import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link, useParams } from "react-router-dom";
import axios from "axios";
import { Menu, X, ArrowRight, MapPin, Phone, MessageCircle, ShieldCheck, Gauge, Award, Instagram, LogOut, Plus, Trash2, Edit3, Save } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = process.env.REACT_APP_HERO_IMAGE || "";
const DEFAULT_SETTINGS = { company_name: "Fettahoğlu Makine", slogan: "Tekstil üretiminize güç katın", istanbul_phone: "0501 009 69 61", diyarbakir_phone: "0552 290 69 61", instagram: "@fettahoglumakina" };
const wa = (n, text) => `https://wa.me/9${n.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`;
const tel = (n) => `tel:${n.replace(/\s/g, "")}`;
const igUrl = (h) => `https://instagram.com/${h.replace(/^@/, "")}`;
const TR_CITIES = ["Adana","Adıyaman","Afyonkarahisar","Ağrı","Aksaray","Amasya","Ankara","Antalya","Ardahan","Artvin","Aydın","Balıkesir","Bartın","Batman","Bayburt","Bilecik","Bingöl","Bitlis","Bolu","Burdur","Bursa","Çanakkale","Çankırı","Çorum","Denizli","Diyarbakır","Düzce","Edirne","Elazığ","Erzincan","Erzurum","Eskişehir","Gaziantep","Giresun","Gümüşhane","Hakkari","Hatay","Iğdır","Isparta","İstanbul","İzmir","Kahramanmaraş","Karabük","Karaman","Kars","Kastamonu","Kayseri","Kilis","Kırıkkale","Kırklareli","Kırşehir","Kocaeli","Konya","Kütahya","Malatya","Manisa","Mardin","Mersin","Muğla","Muş","Nevşehir","Niğde","Ordu","Osmaniye","Rize","Sakarya","Samsun","Şanlıurfa","Siirt","Sinop","Şırnak","Sivas","Tekirdağ","Tokat","Trabzon","Tunceli","Uşak","Van","Yalova","Yozgat","Zonguldak"];
const trackWhatsApp = (machine_id, city) => axios.post(`${API}/whatsapp-clicks`, { machine_id, city }).catch(() => {});

function useSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  useEffect(() => { axios.get(`${API}/settings`).then(r => r.data && setSettings(r.data)).catch(() => {}); }, []);
  return settings;
}
const SITE_URL = "https://fettahoglumakina.com";
function useSEO(title, description, path = "") {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", value);
    };
    if (description) setMeta("name", "description", description);
    const canonicalUrl = `${SITE_URL}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = canonicalUrl;
    if (title) { setMeta("property", "og:title", title); setMeta("name", "twitter:title", title); }
    if (description) { setMeta("property", "og:description", description); setMeta("name", "twitter:description", description); }
    setMeta("property", "og:url", canonicalUrl);
  }, [title, description, path]);
}
function useFetch(url) {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}${url}`, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData([])); }, [url]);
  return data;
}

function Brand() { return <img className="brand-image" src={HERO} alt="Fettahoğlu Makine" />; }

function Header({ settings }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <div className="topline">
        <span>İstanbul · Diyarbakır · Türkiye geneli satış</span>
        <a href={tel(settings.istanbul_phone)} data-testid="header-phone">{settings.istanbul_phone}</a>
      </div>
      <div className="nav-wrap">
        <Link to="/" className="brand" data-testid="brand-link"><Brand /></Link>
        <button className="menu-btn" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">{open ? <X /> : <Menu />}</button>
        <nav className={open ? "nav open" : "nav"}>
          <Link to="/" onClick={() => setOpen(false)} data-testid="nav-home">Ana Sayfa</Link>
          <Link to="/makineler" onClick={() => setOpen(false)} data-testid="nav-machines">Makineler</Link>
          <Link to="/makineler?condition=new" onClick={() => setOpen(false)} data-testid="nav-new">Sıfır Makineler</Link>
          <Link to="/makineler?condition=second_hand" onClick={() => setOpen(false)} data-testid="nav-used">İkinci El</Link>
          <Link to="/hakkimizda" onClick={() => setOpen(false)} data-testid="nav-about">Hakkımızda</Link>
          <Link to="/iletisim" onClick={() => setOpen(false)} data-testid="nav-contact">İletişim</Link>
        </nav>
      </div>
    </header>
  );
}

function Footer({ settings }) {
  return (
    <footer>
      <div className="footer-grid">
        <div>
          <div className="brand light"><Brand /></div>
          <p>{settings.slogan}.<br />Güvenilir makine çözümleri.</p>
        </div>
        <div>
          <h4>İletişim</h4>
          <a href={tel(settings.istanbul_phone)} data-testid="footer-istanbul-phone">İstanbul · {settings.istanbul_phone}</a>
          <a href={tel(settings.diyarbakir_phone)} data-testid="footer-diyarbakir-phone">Diyarbakır · {settings.diyarbakir_phone}</a>
        </div>
        <div>
          <h4>Sosyal</h4>
          <a href={igUrl(settings.instagram)} target="_blank" rel="noreferrer" data-testid="footer-instagram"><Instagram size={16} /> {settings.instagram}</a>
        </div>
      </div>
      <div className="copyright">© {new Date().getFullYear()} {settings.company_name} · Tüm hakları saklıdır</div>
    </footer>
  );
}

function MachineCard({ m }) {
  return (
    <Link className="machine-card" to={`/makineler/${m.slug}`} data-testid={`machine-card-${m.id}`}>
      <div className="machine-image">
        <img src={m.images?.[0] || HERO} alt={`${m.title} — ${m.brand} ${m.model} · ${m.condition === "new" ? "Sıfır" : "İkinci el"} tekstil makinası`} loading="lazy" />
        {m.featured && <span className="tag gold">Öne Çıkan</span>}
        {m.new_listing && <span className="tag">Yeni</span>}
      </div>
      <div className="machine-copy">
        <div className="eyebrow">{m.condition === "new" ? "Sıfır" : "İkinci El"} · {m.category}</div>
        <h3>{m.title}</h3>
        <p>{m.brand} {m.model}</p>
        <div className="card-foot">
          <span><MapPin size={14} /> {m.city}</span>
          <strong>{m.price_hidden ? "Fiyat için iletişime geçin" : `${m.price?.toLocaleString("tr-TR")} ₺`}</strong>
        </div>
      </div>
    </Link>
  );
}

function Home() {
  const settings = useSettings();
  const machines = useFetch("/machines");
  const igPosts = useFetch("/instagram-posts");
  useSEO(`Fettahoğlu Makina | Makina ve Ekipman Çözümleri`, "Fettahoğlu Makina — Sıfır ve ikinci el tekstil makinaları, kesim ve pastal serim ekipmanları. İstanbul ve Diyarbakır ofislerimizden Türkiye geneli satış ve destek.", "/");
  return (
    <>
      <Header settings={settings} />
      <main>
        <section className="hero">
          <img src={HERO} alt={`${settings.company_name} tekstil üretim makinası`} />
          <div className="hero-overlay">
            <div className="eyebrow gold-text">FETTAHOĞLU MAKİNA · TEKSTİL ÇÖZÜMLERİ</div>
            <h1>Fettahoğlu Makina<br /><em>tekstil üretiminize güç katar.</em></h1>
            <p>Sıfır ve ikinci el tekstil makinaları, kesim ve pastal serim ekipmanlarında güvenilir çözüm ortağınız.</p>
            <div className="actions">
              <Link className="btn gold-btn" to="/makineler" data-testid="hero-machines-button">Makineleri İncele <ArrowRight size={17} /></Link>
              <a className="btn outline-btn" href={wa(settings.istanbul_phone, "Merhaba, tekstil makineleri hakkında bilgi almak istiyorum.")} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button"><MessageCircle size={17} /> WhatsApp'tan Bilgi Al</a>
            </div>
          </div>
          <div className="hero-caption">PRECISION · PERFORMANCE · TRUST</div>
        </section>
        <section className="intro section">
          <div>
            <div className="eyebrow gold-text">MAKİNE PORTFÖYÜ</div>
            <h2>Üretim hattınız için<br /><span>doğru güç.</span></h2>
          </div>
          <p className="section-lead">İhtiyacınıza göre seçilmiş, verimli ve güvenilir tekstil makineleri. Her makinede uzman desteği, her adımda şeffaf iletişim.</p>
        </section>
        <section className="category-strip">
          <Link to="/makineler?condition=new" data-testid="category-new"><span>01</span><strong>Sıfır Tekstil<br />Makineleri</strong><ArrowRight /></Link>
          <Link to="/makineler?condition=second_hand" data-testid="category-used"><span>02</span><strong>İkinci El<br />Makineler</strong><ArrowRight /></Link>
          <Link to="/makineler?category=Kesim Makineleri" data-testid="category-cutting"><span>03</span><strong>Kesim<br />Makineleri</strong><ArrowRight /></Link>
          <Link to="/makineler?category=Pastal Serim Makineleri" data-testid="category-laying"><span>04</span><strong>Pastal Serim<br />Makineleri</strong><ArrowRight /></Link>
        </section>
        <section className="section">
          <div className="section-heading">
            <div>
              <div className="eyebrow gold-text">SEÇİLMİŞ ÜRÜNLER</div>
              <h2>Öne çıkan <span>makineler</span></h2>
            </div>
            <Link to="/makineler" className="text-link" data-testid="featured-see-all">Tüm makineleri gör <ArrowRight size={15} /></Link>
          </div>
          <div className="machine-grid">
            {machines?.filter(m => m.featured).slice(0, 3).map(m => <MachineCard key={m.id} m={m} />)}
          </div>
        </section>
        <section className="values">
          <div className="section">
            <div className="eyebrow gold-text">NEDEN {settings.company_name.toUpperCase()}</div>
            <h2>İşiniz için <span>güven.</span></h2>
            <div className="value-grid">
              <Value icon={<Award />} title="Kalite" text="Üretim sürecinize uygun, seçilmiş makineler." />
              <Value icon={<ShieldCheck />} title="Güven" text="Şeffaf iletişim ve satış sonrası destek." />
              <Value icon={<Gauge />} title="Performans" text="Verimliliği artıran güçlü çözümler." />
            </div>
          </div>
        </section>
        {igPosts && igPosts.length > 0 && (
          <section className="section instagram-strip" data-testid="instagram-strip">
            <div className="section-heading">
              <div>
                <div className="eyebrow gold-text">SOSYAL VİTRİN</div>
                <h2>Instagram'da <span>{settings.instagram}</span></h2>
              </div>
              <a href={igUrl(settings.instagram)} target="_blank" rel="noreferrer" className="text-link" data-testid="instagram-follow-link">Bizi takip et <Instagram size={15} /></a>
            </div>
            <div className="ig-grid">
              {igPosts.slice(0, 6).map(p => (
                <a key={p.id} href={p.link || igUrl(settings.instagram)} target="_blank" rel="noreferrer" className="ig-tile" data-testid={`instagram-post-${p.id}`}>
                  <img src={p.image_url} alt={p.caption || "Instagram"} />
                  <div className="ig-overlay"><Instagram size={22} />{p.caption && <span>{p.caption}</span>}</div>
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer settings={settings} />
    </>
  );
}
function Value({ icon, title, text }) { return <div className="value"><div className="value-icon">{icon}</div><h3>{title}</h3><p>{text}</p></div>; }

function Machines() {
  const settings = useSettings();
  const params = new URLSearchParams(window.location.search);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ condition: params.get("condition") || "", category: params.get("category") || "", city: "" });
  const query = `/machines?q=${encodeURIComponent(q)}&condition=${encodeURIComponent(filters.condition)}&category=${encodeURIComponent(filters.category)}&city=${encodeURIComponent(filters.city)}`;
  const machines = useFetch(query);
  const categories = useFetch("/categories");
  useSEO(`Makinalar | Fettahoğlu Makina`, "Fettahoğlu Makina tekstil makina kataloğu — sıfır ve ikinci el kesim, pastal serim ve kumaş makinalarını marka, kategori ve şehre göre filtreleyin.", "/makineler");
  return (
    <>
      <Header settings={settings} />
      <main className="catalog-page">
        <div className="page-intro">
          <div className="eyebrow gold-text">MAKİNA KATALOĞU</div>
          <h1>Fettahoğlu Makina <em>tekstil ekipman kataloğu</em></h1>
          <p>İhtiyacınıza uygun makinayı filtreleyin, sıfır ve ikinci el seçenekleri karşılaştırın, detayları inceleyin.</p>
        </div>
        <div className="filters">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Makine, marka veya model ara..." data-testid="machine-search-input" />
          <select value={filters.condition} onChange={e => setFilters({ ...filters, condition: e.target.value })} data-testid="condition-filter">
            <option value="">Tüm durumlar</option><option value="new">Sıfır</option><option value="second_hand">İkinci El</option>
          </select>
          <select value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })} data-testid="category-filter">
            <option value="">Tüm kategoriler</option>
            {(categories || []).map(c => <option key={c.id}>{c.name}</option>)}
          </select>
          <select value={filters.city} onChange={e => setFilters({ ...filters, city: e.target.value })} data-testid="city-filter">
            <option value="">Tüm şehirler</option>
            {TR_CITIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="results-meta"><span>{machines?.length || 0} makine listeleniyor</span><span>Güncel katalog</span></div>
        <div className="machine-grid">{machines?.map(m => <MachineCard key={m.id} m={m} />)}</div>
        {machines?.length === 0 && <div className="empty">Aramanıza uygun makine bulunamadı.</div>}
      </main>
      <Footer settings={settings} />
    </>
  );
}

function Detail() {
  const settings = useSettings();
  const { slug } = useParams();
  const m = useFetch(`/machines/${slug}`);
  const [active, setActive] = useState(0);
  useSEO(m ? `${m.title} — ${m.brand} ${m.model} | Fettahoğlu Makina` : `Makina Detayı | Fettahoğlu Makina`, m ? `${m.title} — ${m.brand} ${m.model}. ${m.category}, ${m.condition === "new" ? "Sıfır" : "İkinci el"}, ${m.city}. Fettahoğlu Makina güvencesiyle.` : "", m ? `/makineler/${m.slug}` : "/makineler");
  if (!m) return <><Header settings={settings} /><div className="loading">Makine bilgileri yükleniyor...</div></>;
  const message = `Merhaba, ${settings.company_name} web sitenizde bulunan ${m.title} / ${m.model} hakkında bilgi almak istiyorum.`;
  return (
    <>
      <Header settings={settings} />
      <main className="detail-page">
        <Link to="/makineler" className="back-link" data-testid="detail-back">← Makine kataloğuna dön</Link>
        <div className="detail-grid">
          <div>
            <div className="detail-main"><img src={m.images?.[active] || HERO} alt={`${m.title} — ${m.brand} ${m.model} · Fettahoğlu Makina`} /></div>
            <div className="thumbs">{(m.images || [HERO]).map((img, i) => <button onClick={() => setActive(i)} className={active === i ? "active" : ""} key={img + i} data-testid={`gallery-thumb-${i}`}><img src={img} alt={`${m.title} fotoğraf ${i + 1}`} /></button>)}</div>
          </div>
          <div className="detail-copy">
            <div className="eyebrow gold-text">{m.condition === "new" ? "SIFIR MAKİNE" : "İKİNCİ EL MAKİNE"} · {m.category}</div>
            <h1>{m.title}</h1>
            <p className="model">{m.brand} {m.model}</p>
            <div className="detail-price">{m.price_hidden ? "Fiyat bilgisi için iletişime geçin" : `${m.price?.toLocaleString("tr-TR")} ₺`}</div>
            <div className="specs">
              <Spec label="Konum" value={m.city} />
              <Spec label="Üretim yılı" value={m.year || "Belirtilmedi"} />
              <Spec label="Çalışma saati" value={m.working_hours ? `${m.working_hours.toLocaleString("tr-TR")} saat` : "Belirtilmedi"} />
            </div>
            <div className="detail-actions">
              <a onClick={() => trackWhatsApp(m.id, "İstanbul")} href={wa(settings.istanbul_phone, message)} target="_blank" rel="noreferrer" className="btn whatsapp-btn" data-testid="detail-istanbul-whatsapp"><MessageCircle size={18} /> İstanbul WhatsApp</a>
              <a onClick={() => trackWhatsApp(m.id, "Diyarbakır")} href={wa(settings.diyarbakir_phone, message)} target="_blank" rel="noreferrer" className="btn outline-dark" data-testid="detail-diyarbakir-whatsapp"><MessageCircle size={18} /> Diyarbakır WhatsApp</a>
              <a href={tel(settings.istanbul_phone)} className="phone-link" data-testid="detail-call"><Phone size={16} /> Telefonla ara · {settings.istanbul_phone}</a>
            </div>
          </div>
        </div>
        <div className="description">
          <div><div className="eyebrow gold-text">MAKİNE DETAYLARI</div><h2>Teknik bilgi</h2></div>
          <div>
            <p>{m.description}</p>
            <ul>{m.technical_specs?.map(s => <li key={s}>{s}</li>)}</ul>
          </div>
        </div>
      </main>
      <Footer settings={settings} />
    </>
  );
}
function Spec({ label, value }) { return <div><span>{label}</span><strong>{value}</strong></div>; }

function About() {
  const settings = useSettings();
  useSEO(`Hakkımızda | Fettahoğlu Makina`, "Fettahoğlu Makina — tekstil makinaları satış ve danışmanlığı. İstanbul ve Diyarbakır ofislerinden Türkiye geneli hizmet.", "/hakkimizda");
  return (
    <>
      <Header settings={settings} />
      <main className="simple-page">
        <div className="eyebrow gold-text">BİZ KİMİZ</div>
        <h1>Fettahoğlu Makina hakkında</h1>
        <p><strong>Fettahoğlu Makina</strong>, tekstil üreticilerinin ihtiyaç duyduğu makina çözümlerine ulaşmasını kolaylaştıran profesyonel bir satış ve danışmanlık firmasıdır.</p>
        <p>Sıfır ve ikinci el tekstil makinalarında doğru seçimi yapmanız için ihtiyaçlarınızı dinliyor, uygun seçenekleri şeffaf bilgilerle sunuyoruz. Amacımız her üretim hattına güvenilir, verimli ve sürdürülebilir bir çözüm kazandırmak.</p>
        <p>İstanbul ve Diyarbakır ofislerimizden Türkiye'nin dört bir yanına makina tedariki yapıyoruz. Her makina seçiminde ihtiyacınıza en uygun modeli birlikte belirliyoruz.</p>
      </main>
      <Footer settings={settings} />
    </>
  );
}

function Contact() {
  const settings = useSettings();
  useSEO(`İletişim | Fettahoğlu Makina`, "Fettahoğlu Makina iletişim — İstanbul ve Diyarbakır telefon, WhatsApp ve Instagram bilgileri.", "/iletisim");
  return (
    <>
      <Header settings={settings} />
      <main className="simple-page contact-page">
        <div className="eyebrow gold-text">İLETİŞİM</div>
        <h1>Fettahoğlu Makina ile iletişim</h1>
        <div className="contact-list">
          <a href={tel(settings.istanbul_phone)} data-testid="contact-istanbul"><span>İSTANBUL</span><strong>{settings.istanbul_phone}</strong><Phone /></a>
          <a href={tel(settings.diyarbakir_phone)} data-testid="contact-diyarbakir"><span>DİYARBAKIR</span><strong>{settings.diyarbakir_phone}</strong><Phone /></a>
          <a href={igUrl(settings.instagram)} target="_blank" rel="noreferrer" data-testid="contact-instagram"><span>INSTAGRAM</span><strong>{settings.instagram}</strong><Instagram /></a>
        </div>
      </main>
      <Footer settings={settings} />
    </>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("admin@fettahoglumakine.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  useSEO("Admin Girişi | Fettahoğlu Makina", "Yönetim paneli girişi.", "/admin");
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "robots"); document.head.appendChild(meta); }
    meta.setAttribute("content", "noindex, nofollow");
  }, []);
  const submit = async e => {
    e.preventDefault();
    try { const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true }); onLogin(r.data); }
    catch (err) { setError(err.response?.data?.detail || "Giriş yapılamadı"); }
  };
  return (
    <main className="login-page">
      <div className="login-brand"><Brand /></div>
      <form onSubmit={submit} className="login-form">
        <div className="eyebrow gold-text">GÜVENLİ YÖNETİM</div>
        <h1>Admin girişi</h1>
        <p>Makine kataloğunuzu buradan yönetin.</p>
        <label>E-posta<input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="admin-email-input" required /></label>
        <label>Şifre<input type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="admin-password-input" required /></label>
        {error && <div className="error" data-testid="admin-login-error">{error}</div>}
        <button className="btn gold-btn full" data-testid="admin-login-submit">Giriş yap <ArrowRight size={16} /></button>
        <p className="login-hint">Şifrenizi giriş yaptıktan sonra panel içindeki <strong>Şifre değiştir</strong> bölümünden güncelleyebilirsiniz.</p>
        <Link to="/" className="back-link">← Siteye dön</Link>
      </form>
    </main>
  );
}

const EMPTY_FORM = { title: "", brand: "", model: "", category: "Diğer Tekstil Makineleri", condition: "second_hand", city: "İstanbul", description: "", technical_specs: [], images: [], price: null, price_hidden: true, year: null, working_hours: null, featured: false, new_listing: false, published: false };

function Admin() {
  const [user, setUser] = useState(null);
  const [machines, setMachines] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const [specInput, setSpecInput] = useState("");
  const [settingsMsg, setSettingsMsg] = useState("");
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdMsg, setPwdMsg] = useState({ text: "", type: "" });
  const [igPosts, setIgPosts] = useState([]);
  const [igForm, setIgForm] = useState({ image_url: "", caption: "", link: "", order: 0 });
  const [igEditing, setIgEditing] = useState(null);
  const [igUploading, setIgUploading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useSEO("Yönetim Paneli | Fettahoğlu Makine", "Admin paneli");

  const refresh = useCallback(() => Promise.all([
    axios.get(`${API}/machines?admin=true`, { withCredentials: true }),
    axios.get(`${API}/admin/stats`, { withCredentials: true }),
    axios.get(`${API}/categories`),
    axios.get(`${API}/admin/whatsapp-clicks`, { withCredentials: true }),
    axios.get(`${API}/settings`),
    axios.get(`${API}/instagram-posts`)
  ]).then(([m, s, c, w, st, ig]) => { setMachines(m.data); setStats(s.data); setCategories(c.data); setClicks(w.data); setSiteSettings(st.data); setIgPosts(ig.data); }).catch(() => {}), []);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(r => { setUser(r.data); refresh(); }).catch(() => setUser(false));
  }, [refresh]);

  if (user === null) return <div className="loading">Güvenli oturum kontrol ediliyor...</div>;
  if (!user) return <Login onLogin={u => { setUser(u); refresh(); }} />;

  const resetForm = () => { setEditing(null); setForm(EMPTY_FORM); setSpecInput(""); };

  const save = async e => {
    e.preventDefault();
    const payload = { ...form, price: form.price === "" ? null : form.price, year: form.year === "" ? null : form.year, working_hours: form.working_hours === "" ? null : form.working_hours };
    if (editing) await axios.put(`${API}/machines/${editing}`, payload, { withCredentials: true });
    else await axios.post(`${API}/machines`, payload, { withCredentials: true });
    resetForm();
    refresh();
  };

  const upload = async e => {
    const files = [...e.target.files];
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(file => { const body = new FormData(); body.append("file", file); return axios.post(`${API}/upload`, body, { withCredentials: true }).then(r => r.data.url); }));
      setForm(f => ({ ...f, images: [...f.images, ...urls] }));
    } finally { setUploading(false); e.target.value = ""; }
  };
  const makePrimary = i => setForm(f => ({ ...f, images: [f.images[i], ...f.images.filter((_, n) => n !== i)] }));
  const removeImage = i => setForm(f => ({ ...f, images: f.images.filter((_, n) => n !== i) }));
  const addSpec = () => { if (specInput.trim()) { setForm(f => ({ ...f, technical_specs: [...f.technical_specs, specInput.trim()] })); setSpecInput(""); } };
  const removeSpec = i => setForm(f => ({ ...f, technical_specs: f.technical_specs.filter((_, n) => n !== i) }));

  const addCategory = async e => {
    e.preventDefault();
    if (newCategory.trim()) { await axios.post(`${API}/categories`, { name: newCategory.trim() }, { withCredentials: true }); setNewCategory(""); refresh(); }
  };
  const remove = async id => { if (window.confirm("Bu makineyi silmek istediğinize emin misiniz?")) { await axios.delete(`${API}/machines/${id}`, { withCredentials: true }); refresh(); } };
  const saveSettings = async e => {
    e.preventDefault();
    await axios.put(`${API}/settings`, siteSettings, { withCredentials: true });
    setSettingsMsg("Ayarlar kaydedildi.");
    setTimeout(() => setSettingsMsg(""), 3000);
  };

  const changePassword = async e => {
    e.preventDefault();
    setPwdMsg({ text: "", type: "" });
    if (pwd.new_password.length < 8) return setPwdMsg({ text: "Yeni şifre en az 8 karakter olmalı.", type: "error" });
    if (pwd.new_password !== pwd.confirm) return setPwdMsg({ text: "Yeni şifreler eşleşmiyor.", type: "error" });
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwd.current_password, new_password: pwd.new_password }, { withCredentials: true });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      setPwdMsg({ text: "Şifreniz güncellendi. Bir sonraki girişte yeni şifrenizi kullanın.", type: "success" });
    } catch (err) {
      setPwdMsg({ text: err.response?.data?.detail || "Şifre değiştirilemedi.", type: "error" });
    }
  };

  const saveIg = async e => {
    e.preventDefault();
    if (!igForm.image_url) return;
    if (igEditing) await axios.put(`${API}/instagram-posts/${igEditing}`, igForm, { withCredentials: true });
    else await axios.post(`${API}/instagram-posts`, igForm, { withCredentials: true });
    setIgEditing(null); setIgForm({ image_url: "", caption: "", link: "", order: igPosts.length });
    refresh();
  };
  const uploadIg = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIgUploading(true);
    try {
      const body = new FormData(); body.append("file", file);
      const r = await axios.post(`${API}/upload`, body, { withCredentials: true });
      setIgForm(f => ({ ...f, image_url: r.data.url }));
    } finally { setIgUploading(false); e.target.value = ""; }
  };
  const removeIg = async id => { if (window.confirm("Bu gönderiyi silmek istediğinize emin misiniz?")) { await axios.delete(`${API}/instagram-posts/${id}`, { withCredentials: true }); refresh(); } };

  return (
    <main className="admin-page">
      <aside className="admin-side">
        <div className="login-brand"><Brand /></div>
        <div className="admin-label">YÖNETİM PANELİ</div>
        <Link to="/" data-testid="admin-view-site">Siteyi görüntüle <ArrowRight size={15} /></Link>
        <button onClick={() => axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).finally(() => setUser(false))} data-testid="admin-logout"><LogOut size={16} /> Çıkış yap</button>
      </aside>
      <section className="admin-content">
        <div className="admin-head">
          <div>
            <div className="eyebrow gold-text">GENEL BAKIŞ</div>
            <h1>Merhaba, yönetici.</h1>
          </div>
          <button className="btn gold-btn" onClick={resetForm} data-testid="new-machine-button"><Plus size={17} /> Yeni makine</button>
        </div>
        <div className="stats">
          {[["Toplam makine", stats?.total], ["Yayında", stats?.published], ["Taslak", stats?.drafts], ["Öne çıkan", stats?.featured], ["Sıfır", stats?.new], ["İkinci el", stats?.second_hand], ["WhatsApp tıklaması", clicks.length]].map(([a, b]) => <div key={a}><span>{a}</span><strong>{b ?? "—"}</strong></div>)}
        </div>

        <div className="admin-layout">
          <div className="admin-list">
            <div className="list-head"><h2>Makine yönetimi</h2><span>{machines.length} kayıt</span></div>
            {machines.map(m => (
              <div className="admin-row" key={m.id}>
                <img src={m.images?.[0] || HERO} alt="" />
                <div>
                  <strong>{m.title}</strong>
                  <span>{m.brand} {m.model} · {m.published ? "Yayında" : "Taslak"} · {m.condition === "new" ? "Sıfır" : "İkinci el"}</span>
                </div>
                <button onClick={() => { setEditing(m.id); setForm({ ...EMPTY_FORM, ...m, images: m.images || [], technical_specs: m.technical_specs || [] }); }} data-testid={`edit-machine-${m.id}`}><Edit3 size={16} /></button>
                <button onClick={() => remove(m.id)} data-testid={`delete-machine-${m.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>

          <form className="machine-form" onSubmit={save}>
            <div className="eyebrow gold-text">{editing ? "DÜZENLE" : "YENİ KAYIT"}</div>
            <h2>{editing ? "Makineyi düzenle" : "Makine ekle"}</h2>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Makine adı *" data-testid="machine-title-input" required />
            <div className="two-col">
              <input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} placeholder="Marka" data-testid="machine-brand-input" />
              <input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder="Model" data-testid="machine-model-input" />
            </div>
            <div className="two-col">
              <select value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })} data-testid="machine-condition-input">
                <option value="new">Sıfır</option><option value="second_hand">İkinci El</option>
              </select>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="machine-category-input">
                {categories.map(c => <option key={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="two-col">
              <select value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="machine-city-input">
                {TR_CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <input type="number" value={form.year || ""} onChange={e => setForm({ ...form, year: e.target.value ? Number(e.target.value) : null })} placeholder="Üretim yılı" data-testid="machine-year-input" />
            </div>
            <div className="two-col">
              <input type="number" value={form.price || ""} onChange={e => setForm({ ...form, price: e.target.value ? Number(e.target.value) : null })} placeholder="Fiyat (₺)" data-testid="machine-price-input" />
              <input type="number" value={form.working_hours || ""} onChange={e => setForm({ ...form, working_hours: e.target.value ? Number(e.target.value) : null })} placeholder="Çalışma saati" data-testid="machine-hours-input" />
            </div>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Açıklama" data-testid="machine-description-input" />

            <div className="spec-input">
              <input value={specInput} onChange={e => setSpecInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSpec(); } }} placeholder="Teknik özellik ekle ve Enter'a bas" data-testid="machine-spec-input" />
              <button type="button" onClick={addSpec} className="btn gold-btn" data-testid="add-spec-button"><Plus size={14} /></button>
            </div>
            {form.technical_specs.length > 0 && (
              <ul className="spec-chips">{form.technical_specs.map((s, i) => <li key={s + i}>{s}<button type="button" onClick={() => removeSpec(i)} data-testid={`remove-spec-${i}`}><X size={12} /></button></li>)}</ul>
            )}

            <label className="upload-label">Fotoğraflar<input type="file" accept="image/*" multiple onChange={upload} data-testid="machine-images-input" /></label>
            {uploading && <span className="uploading">Fotoğraflar yükleniyor...</span>}
            {form.images.length > 0 && (
              <div className="image-manager">{form.images.map((img, i) => (
                <div className={i === 0 ? "image-item primary" : "image-item"} key={`${img}-${i}`}>
                  <img src={img} alt={`Fotoğraf ${i + 1}`} />
                  <button type="button" onClick={() => makePrimary(i)} data-testid={`set-primary-image-${i}`}>{i === 0 ? "Ana fotoğraf" : "Ana yap"}</button>
                  <button type="button" onClick={() => removeImage(i)} data-testid={`remove-machine-image-${i}`}>Sil</button>
                </div>
              ))}</div>
            )}

            <label className="check"><input type="checkbox" checked={form.price_hidden} onChange={e => setForm({ ...form, price_hidden: e.target.checked })} data-testid="machine-price-hidden-input" /> Fiyatı gizle (İletişime geçin göster)</label>
            <label className="check"><input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} data-testid="machine-published-input" /> Yayında göster</label>
            <label className="check"><input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} data-testid="machine-featured-input" /> Öne çıkar</label>
            <label className="check"><input type="checkbox" checked={form.new_listing} onChange={e => setForm({ ...form, new_listing: e.target.checked })} data-testid="machine-new-listing-input" /> Yeni etiketi</label>
            <button className="btn gold-btn full" data-testid="save-machine-button">Kaydet <ArrowRight size={16} /></button>
          </form>
        </div>

        <div className="admin-secondary">
          <section className="admin-list">
            <div className="list-head"><h2>Kategori yönetimi</h2></div>
            <form className="category-add" onSubmit={addCategory}>
              <input value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Yeni kategori" data-testid="new-category-input" />
              <button className="btn gold-btn" data-testid="add-category-button"><Plus size={15} /></button>
            </form>
            {categories.map(c => (
              <div className="category-row" key={c.id}>
                <span>{c.name}</span>
                <button onClick={() => { if (window.confirm("Bu kategoriyi silmek istediğinize emin misiniz?")) axios.delete(`${API}/categories/${c.id}`, { withCredentials: true }).then(refresh); }} data-testid={`delete-category-${c.id}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </section>

          <section className="admin-list">
            <div className="list-head"><h2>WhatsApp talepleri</h2><span>{clicks.length} tıklama</span></div>
            {clicks.slice(0, 8).map(c => (
              <div className="click-row" key={c.id}>
                <strong>{c.machine_title}</strong>
                <span>{c.city} · {new Date(c.created_at).toLocaleString("tr-TR")} · {c.count} toplam tıklama</span>
              </div>
            ))}
            {!clicks.length && <p className="muted-text">Henüz WhatsApp tıklaması yok.</p>}
          </section>
        </div>

        {siteSettings && (
          <section className="admin-list settings-block">
            <div className="list-head"><h2>Site ayarları</h2><span>Firma bilgileri</span></div>
            <form className="settings-form" onSubmit={saveSettings}>
              <label>Firma adı<input value={siteSettings.company_name} onChange={e => setSiteSettings({ ...siteSettings, company_name: e.target.value })} data-testid="settings-company-input" /></label>
              <label>Slogan<input value={siteSettings.slogan} onChange={e => setSiteSettings({ ...siteSettings, slogan: e.target.value })} data-testid="settings-slogan-input" /></label>
              <label>İstanbul telefon<input value={siteSettings.istanbul_phone} onChange={e => setSiteSettings({ ...siteSettings, istanbul_phone: e.target.value })} data-testid="settings-istanbul-input" /></label>
              <label>Diyarbakır telefon<input value={siteSettings.diyarbakir_phone} onChange={e => setSiteSettings({ ...siteSettings, diyarbakir_phone: e.target.value })} data-testid="settings-diyarbakir-input" /></label>
              <label>Instagram<input value={siteSettings.instagram} onChange={e => setSiteSettings({ ...siteSettings, instagram: e.target.value })} data-testid="settings-instagram-input" /></label>
              <button className="btn gold-btn" data-testid="save-settings-button"><Save size={15} /> Ayarları kaydet</button>
              {settingsMsg && <span className="settings-msg" data-testid="settings-msg">{settingsMsg}</span>}
            </form>
          </section>
        )}

        <section className="admin-list settings-block">
          <div className="list-head"><h2>Şifre değiştir</h2><span>Hesap güvenliği</span></div>
          <form className="settings-form" onSubmit={changePassword}>
            <label>Mevcut şifre<input type="password" value={pwd.current_password} onChange={e => setPwd({ ...pwd, current_password: e.target.value })} data-testid="current-password-input" required autoComplete="current-password" /></label>
            <label>Yeni şifre (en az 8 karakter)<input type="password" value={pwd.new_password} onChange={e => setPwd({ ...pwd, new_password: e.target.value })} data-testid="new-password-input" required autoComplete="new-password" minLength={8} /></label>
            <label>Yeni şifre (tekrar)<input type="password" value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} data-testid="confirm-password-input" required autoComplete="new-password" minLength={8} /></label>
            <button className="btn gold-btn" data-testid="change-password-button"><Save size={15} /> Şifreyi güncelle</button>
            {pwdMsg.text && <span className={pwdMsg.type === "error" ? "error settings-msg" : "settings-msg"} data-testid="password-msg">{pwdMsg.text}</span>}
          </form>
        </section>

        <section className="admin-list settings-block">
          <div className="list-head"><h2>Instagram vitrini</h2><span>Ana sayfa şeridi · en fazla 6 gönderi</span></div>
          <form className="settings-form ig-form" onSubmit={saveIg}>
            <label className="ig-image-label">Fotoğraf
              <input type="file" accept="image/*" onChange={uploadIg} data-testid="ig-image-file" />
              <input placeholder="veya doğrudan URL yapıştırın" value={igForm.image_url} onChange={e => setIgForm({ ...igForm, image_url: e.target.value })} data-testid="ig-image-url" />
            </label>
            {igUploading && <span className="uploading">Fotoğraf yükleniyor...</span>}
            {igForm.image_url && <div className="ig-preview"><img src={igForm.image_url} alt="Önizleme" /></div>}
            <label>Açıklama (opsiyonel)<input value={igForm.caption} onChange={e => setIgForm({ ...igForm, caption: e.target.value })} data-testid="ig-caption-input" /></label>
            <label>Instagram gönderi linki (opsiyonel)<input value={igForm.link} onChange={e => setIgForm({ ...igForm, link: e.target.value })} placeholder="https://instagram.com/p/..." data-testid="ig-link-input" /></label>
            <label>Sıra<input type="number" value={igForm.order} onChange={e => setIgForm({ ...igForm, order: Number(e.target.value) || 0 })} data-testid="ig-order-input" /></label>
            <button className="btn gold-btn" data-testid="save-ig-button"><Save size={15} /> {igEditing ? "Gönderiyi güncelle" : "Gönderi ekle"}</button>
            {igEditing && <button type="button" className="btn outline-dark" onClick={() => { setIgEditing(null); setIgForm({ image_url: "", caption: "", link: "", order: igPosts.length }); }} data-testid="cancel-ig-edit">Vazgeç</button>}
          </form>
          <div className="ig-admin-grid">
            {igPosts.map(p => (
              <div className="ig-admin-tile" key={p.id}>
                <img src={p.image_url} alt={p.caption || "Instagram"} />
                <div className="ig-admin-body">
                  <strong>{p.caption || "Açıklama yok"}</strong>
                  <span>Sıra: {p.order}</span>
                </div>
                <div className="ig-admin-actions">
                  <button onClick={() => { setIgEditing(p.id); setIgForm({ image_url: p.image_url, caption: p.caption || "", link: p.link || "", order: p.order || 0 }); }} data-testid={`edit-ig-${p.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => removeIg(p.id)} data-testid={`delete-ig-${p.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {!igPosts.length && <p className="muted-text">Henüz gönderi eklenmedi. Yukarıdan ilk gönderiyi ekleyin.</p>}
          </div>
        </section>
      </section>
    </main>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/makineler" element={<Machines />} />
        <Route path="/makineler/:slug" element={<Detail />} />
        <Route path="/hakkimizda" element={<About />} />
        <Route path="/iletisim" element={<Contact />} />
        <Route path="/admin/*" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

function NotFound() {
  const settings = useSettings();
  useSEO("Sayfa bulunamadı | Fettahoğlu Makina", "Aradığınız sayfa bulunamadı.", "/404");
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "robots"); document.head.appendChild(meta); }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex, follow");
    return () => { if (prev) meta.setAttribute("content", prev); };
  }, []);
  return (
    <>
      <Header settings={settings} />
      <main className="simple-page">
        <div className="eyebrow gold-text">404</div>
        <h1>Sayfa bulunamadı</h1>
        <p>Aradığınız sayfa taşınmış veya kaldırılmış olabilir. Ana sayfadan devam edin veya <Link to="/makineler" className="text-link" data-testid="notfound-catalog-link">makina kataloğunu</Link> inceleyin.</p>
        <p><Link to="/" className="btn gold-btn" data-testid="notfound-home-link">Ana sayfaya dön <ArrowRight size={15} /></Link></p>
      </main>
      <Footer settings={settings} />
    </>
  );
}
